import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'

interface BlockingNotification {
  id: string
  display_name: string | null
}

interface BlockedResponseBody {
  notices: { contact_group_referenced_by_active_notification?: string }
  result: { blocking_notifications: BlockingNotification[] }
}

interface ArchivedResponseBody {
  result: { redirect_url: string }
}

type ArchiveOpenEvent = Event & { params: { url: string, name: string } }

export default class extends Controller {
  static targets = ['modal', 'confirmHeading', 'confirmBody', 'archiveButton', 'blockedSection', 'blockedBody', 'blockedList', 'error']

  declare readonly modalTarget: HTMLElement
  declare readonly confirmHeadingTarget: HTMLElement
  declare readonly confirmBodyTarget: HTMLElement
  declare readonly archiveButtonTarget: HTMLElement
  declare readonly blockedSectionTarget: HTMLElement
  declare readonly blockedBodyTarget: HTMLElement
  declare readonly blockedListTarget: HTMLElement
  declare readonly errorTarget: HTMLElement

  private modal!: Modal
  private pendingUrl = ''

  connect (): void {
    this.modal = new Modal(this.modalTarget)
  }

  open (event: ArchiveOpenEvent): void {
    event.preventDefault()
    this.pendingUrl = event.params.url

    this.confirmHeadingTarget.textContent = (this.confirmHeadingTarget.dataset.template ?? '').replace('%name%', event.params.name)
    this.confirmBodyTarget.textContent = (this.confirmBodyTarget.dataset.template ?? '').replace('%name%', event.params.name)
    this.blockedSectionTarget.style.display = 'none'
    this.errorTarget.style.display = 'none'
    this.archiveButtonTarget.style.display = ''

    this.modal.show()
  }

  async confirm (): Promise<void> {
    try {
      const response = await fetchWithHandlingLoginRedirect(this.pendingUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
      // Navigated to explicitly, rather than relying on fetch's own redirect-following: fetch
      // would otherwise read (and clear) the success flash before this navigation could render it.
      const body = await response.json() as ArchivedResponseBody
      window.location.href = body.result.redirect_url
    } catch (e) {
      await this.handleFailure(e)
    }
  }

  private async handleFailure (error: unknown): Promise<void> {
    const response = error as { status?: number, json?: () => Promise<BlockedResponseBody> }
    if (response.status === 409 && typeof response.json === 'function') {
      try {
        this.showBlocked(await response.json())
        return
      } catch {
        // Malformed 409 body: fall through to the generic failure message below.
      }
    }
    this.showFailure()
  }

  private showFailure (): void {
    this.errorTarget.style.display = ''
  }

  private showBlocked (body: BlockedResponseBody): void {
    this.archiveButtonTarget.style.display = 'none'
    this.blockedBodyTarget.textContent = body.notices.contact_group_referenced_by_active_notification ?? ''
    this.blockedListTarget.innerHTML = ''
    body.result.blocking_notifications.forEach((notification) => {
      const item = document.createElement('li')
      item.textContent = notification.display_name ?? this.blockedListTarget.dataset.hiddenLabel ?? ''
      this.blockedListTarget.appendChild(item)
    })
    this.blockedSectionTarget.style.display = ''
  }
}
